/**
 * #743: WhatsApp-style reply to one exact Main Command message.
 *
 * The owner picks Reply on a participant's message (Overseer, Observer UX / Core, a Worker, a
 * plan) or on an earlier owner message. The outgoing message carries a canonical reference to
 * that one row - never copied text - and the quote shown above the composer and on the sent
 * message is read from the same canonical row. Recipients stay the explicit target fields: a
 * quote is context, never routing authority.
 */
import type { SharedTimelineItem } from './shared-command-timeline';

export type MainCommandReplyKind = 'owner_command' | 'observer_message' | 'worker_message' | 'plan';

export interface MainCommandReplyRef {
  readonly kind: MainCommandReplyKind;
  readonly id: string;
}

/** What the composer shows while a reply is being written. */
export interface MainCommandReplyDraft {
  readonly ref: MainCommandReplyRef;
  readonly author: string;
  readonly text: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const KINDS: readonly MainCommandReplyKind[] = ['owner_command', 'observer_message', 'worker_message', 'plan'];

/** The stored owner_commands.reply_to value, or null when it is absent or not the known shape. */
export function parseReplyRef(value: unknown): MainCommandReplyRef | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  const kind = row['kind'];
  const id = row['id'];
  if (typeof kind !== 'string' || !KINDS.includes(kind as MainCommandReplyKind)) return null;
  if (typeof id !== 'string' || !UUID.test(id)) return null;
  return { kind: kind as MainCommandReplyKind, id };
}

export function replyRefKey(ref: MainCommandReplyRef): string {
  return `${ref.kind}:${ref.id}`;
}

/**
 * The canonical row a timeline message is. Computed lines the screen wrote itself (a direct
 * answer, a control receipt, a note) are not stored messages, so they have no reference.
 */
export function replyRefForItem(item: SharedTimelineItem): MainCommandReplyRef | null {
  const colon = item.id.indexOf(':');
  const prefix = colon < 0 ? '' : item.id.slice(0, colon);
  const rest = colon < 0 ? item.id : item.id.slice(colon + 1);
  if (prefix === 'observer' && UUID.test(rest)) return { kind: 'observer_message', id: rest };
  if (prefix === 'worker' && UUID.test(rest)) return { kind: 'worker_message', id: rest };
  const turn = item.commandTurn;
  if (turn === null) return null;
  if (turn.kind === 'command' && item.mine && UUID.test(turn.id)) return { kind: 'owner_command', id: turn.id };
  if (turn.kind === 'plan' && UUID.test(turn.id)) return { kind: 'plan', id: turn.id };
  return null;
}

/** Who said it, for the quote: the byline without its time. */
export function replyAuthor(item: SharedTimelineItem): string {
  if (item.mine) return 'You';
  const byline = item.byline.split(' · ')[0]?.trim() ?? '';
  return byline === '' ? 'Earlier message' : byline;
}

/** A quote reads as plain words: Markdown markers the reply rendered are dropped. */
export function plainQuoteText(text: string): string {
  return text
    .replace(/^\s{0,3}(?:#{1,6}\s+|[-*+]\s+|\d+[.)]\s+|>\s?)/gm, '')
    .replace(/\*\*([^*]+)\*\*|__([^_]+)__/g, '$1$2')
    .replace(/`([^`]+)`/g, '$1');
}

export function clipQuote(text: string, max = 160): string {
  const flat = plainQuoteText(text).replace(/\s+/g, ' ').trim();
  return flat.length <= max ? flat : `${flat.slice(0, max - 1).trimEnd()}…`;
}

export function replyDraftForItem(item: SharedTimelineItem): MainCommandReplyDraft | null {
  const ref = replyRefForItem(item);
  if (ref === null) return null;
  return { ref, author: replyAuthor(item), text: clipQuote(item.text) };
}

/** Every quotable message on screen by canonical reference, so a sent reply can show its quote. */
export function replyQuotes(items: readonly SharedTimelineItem[]): ReadonlyMap<string, MainCommandReplyDraft> {
  const out = new Map<string, MainCommandReplyDraft>();
  for (const item of items) {
    const draft = replyDraftForItem(item);
    if (draft !== null) out.set(replyRefKey(draft.ref), draft);
  }
  return out;
}
